import React from "react";
import { formatThaiDate } from "../utils";
import constant from "../../../../constants";
import { ScrollArea } from "../../../../components/ui";
import {
  FiPackage,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiTruck,
  FiCreditCard,
} from "react-icons/fi";

export const OrderTimeline = ({ orderInfo }) => {
  if (!orderInfo) return null;

  const isApproved = orderInfo.status === constant.statuses.APPROVE;
  const isCanceled = orderInfo.status === constant.statuses.CANCEL;
  const hasSlips = (orderInfo.slips?.length || 0) > 0;
  const isMail = orderInfo.deliverChannel === "mail";
  const isShipped = orderInfo.shippingStatus === constant.shippingStatuses.SHIPPED;

  // Comprehensive extraction of payment/slip timestamp
  const extractPaymentTimestamp = () => {
    const directTime =
      orderInfo.uploadSlipAt ||
      orderInfo.uploadSlipTime ||
      orderInfo.paidAt ||
      orderInfo.paymentTime ||
      orderInfo.paymentSlipTime ||
      orderInfo.slipUploadedAt;
    if (directTime) return directTime;

    const allNotes = [...(orderInfo.notes || []), ...(orderInfo.note || [])];
    const slipNote = allNotes.find(
      (n) =>
        typeof n?.note === "string" &&
        (n.note.includes("slipId") || n.note.toLowerCase().includes("slip"))
    );
    if (slipNote?.time) return slipNote.time;

    const slips = Array.isArray(orderInfo.slips)
      ? orderInfo.slips
      : typeof orderInfo.slips === "string"
        ? [orderInfo.slips]
        : [];
    if (slips.length > 0) {
      const latestSlip = slips[slips.length - 1];
      if (typeof latestSlip === "string") {
        const match =
          latestSlip.match(/\/(\d{13})[_.]/) ||
          latestSlip.match(/%2F(\d{13})[_.]/) ||
          latestSlip.match(/(\d{13})/);
        if (match && match[1]) {
          const ts = Number(match[1]);
          if (!isNaN(ts) && ts > 1500000000000 && ts < 2500000000000) {
            return ts;
          }
        }
      }
    }

    // หากออเดอร์อนุมัติแล้ว หรือมีสลิป แต่ไม่มี timestamp สลิปแยก ให้ใช้เวลาอนุมัติ (approvedAt) แทนได้
    if (orderInfo.approvedAt) {
      return orderInfo.approvedAt;
    }

    if (
      (orderInfo.status === constant.statuses.PAID || isApproved) &&
      orderInfo.updatedAt
    ) {
      return orderInfo.updatedAt;
    }

    if (hasSlips) {
      return orderInfo.createdAt;
    }

    return null;
  };

  const paymentTimestamp = extractPaymentTimestamp();
  const paymentFormattedDate = paymentTimestamp
    ? formatThaiDate(paymentTimestamp)
    : null;

  const paymentDesc = [
    hasSlips ? `แนบสลิปแล้ว (${orderInfo.slips.length} รูป)` : "",
    orderInfo.paymentChannel
      ? `ช่องทาง: ${orderInfo.paymentChannel === "qr"
        ? "QR Code"
        : orderInfo.paymentChannel === "bank"
          ? "โอนผ่านธนาคาร"
          : orderInfo.paymentChannel.toUpperCase()
      }`
      : "รอตรวจสอบการชำระเงิน",
  ]
    .filter(Boolean)
    .join(" • ");

  const steps = [
    {
      id: "created",
      title: "สร้างคำสั่งซื้อ",
      time: orderInfo.createdAt ? formatThaiDate(orderInfo.createdAt) : null,
      desc: orderInfo.createdBy?.userId
        ? `สร้างโดย #${orderInfo.createdBy.userId} ${orderInfo.createdBy.firstName || ""}`
        : orderInfo.isGuest
          ? "สั่งซื้อแบบไม่สมัครสมาชิก"
          : "ลูกค้าสั่งซื้อผ่านหน้าเว็บไซต์",
      icon: <FiPackage className="text-sm" />,
      status: "completed",
    },
    {
      id: "payment",
      title: "แนบสลิป / แจ้งชำระเงิน",
      time: paymentFormattedDate,
      desc: paymentDesc,
      icon: hasSlips ? (
        <FiFileText className="text-sm" />
      ) : (
        <FiCreditCard className="text-sm" />
      ),
      status:
        hasSlips || orderInfo.status === constant.statuses.PAID || isApproved
          ? "completed"
          : "current",
    },
    {
      id: "status",
      title: isCanceled
        ? "ยกเลิกคำสั่งซื้อ"
        : isApproved
          ? "อนุมัติคำสั่งซื้อ"
          : "รออนุมัติคำสั่งซื้อ",
      time: isCanceled
        ? formatThaiDate(orderInfo.canceledAt)
        : isApproved
          ? formatThaiDate(orderInfo.approvedAt)
          : null,
      desc: isCanceled
        ? `ยกเลิกโดย: ${orderInfo.cancelerName || "Vandee AI"}`
        : isApproved
          ? `อนุมัติโดย: ${orderInfo.approverName || "Vandee AI"}`
          : "เจ้าหน้าที่กำลังตรวจสอบรายการและสลิป",
      icon: isCanceled ? (
        <FiXCircle className="text-sm text-rose-500" />
      ) : (
        <FiCheckCircle className="text-sm" />
      ),
      status: isApproved
        ? "completed"
        : isCanceled
          ? "canceled"
          : hasSlips || orderInfo.status === constant.statuses.PAID
            ? "current"
            : "pending",
    },
  ];

  if (isMail) {
    steps.push({
      id: "shipping",
      title: isShipped
        ? "จัดส่งพัสดุไปรษณีย์แล้ว"
        : "เตรียมจัดส่งพัสดุไปรษณีย์",
      time: orderInfo.shippingDate ? `วันที่: ${orderInfo.shippingDate}` : null,
      desc: orderInfo.shippingTrack
        ? `เลขพัสดุ: ${orderInfo.shippingTrack}`
        : `สถานะ: ${constant.shippingStatusesTranslate[orderInfo.shippingStatus] || "รอจัดส่ง"}`,
      icon: <FiTruck className="text-sm" />,
      status: isShipped ? "completed" : isApproved ? "current" : "pending",
    });
  }

  // Find index of current/latest active step
  const activeIndex = steps.reduce((acc, cur, idx) => {
    if (cur.status === "completed" || cur.status === "canceled" || cur.status === "current") {
      return idx;
    }
    return acc;
  }, 0);

  return (
    <div className="relative bg-white w-full rounded-md shadow-xs border border-slate-200 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3.5 border-b border-slate-200 bg-slate-50/60">
        <div className="flex items-center gap-2">
          <span className="flex h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
          <h3 className="font-bold text-base text-slate-800">
            ลำดับสถานะคำสั่งซื้อ
          </h3>
        </div>

      </div>

      {/* Vertical Timeline Body */}
      <ScrollArea className="h-[320px] p-4">
        <div className="flex flex-col">
          {steps.map((step, idx) => {
            const isCompleted = step.status === "completed";
            const isCurrent = step.status === "current" || (idx === activeIndex && !isCanceled);
            const isCanceledStep = step.status === "canceled";
            const isPending = step.status === "pending" && !isCurrent;
            const isLast = idx === steps.length - 1;

            // Determine connector line color based on progression
            const isLineActive = idx < activeIndex;

            return (
              <div key={step.id} className="flex items-start gap-3 relative">
                {/* 1. Left: Outlined Box Icon */}
                <div
                  className={`w-8 h-8 rounded-md border flex items-center justify-center shrink-0 transition-all ${isCanceledStep
                    ? "border-rose-200 bg-rose-50 text-rose-600"
                    : isCompleted || isCurrent
                      ? "border-slate-300 bg-white text-slate-700 shadow-2xs"
                      : "border-slate-200 bg-slate-50/50 text-slate-400 opacity-60"
                    }`}
                >
                  {step.icon}
                </div>

                {/* 2. Middle: Continuous Track & Dot Indicator */}
                <div className="relative flex flex-col items-center self-stretch shrink-0 w-4">
                  {/* Node Dot */}
                  <div className="z-10 mt-2.5 flex items-center justify-center">
                    {isCanceledStep ? (
                      <div className="w-3 h-3 rounded-full bg-rose-600 ring-4 ring-rose-100" />
                    ) : isCurrent ? (
                      <div className="w-3 h-3 rounded-full bg-emerald-500 ring-4 ring-emerald-100 ring-offset-1 animate-pulse" />
                    ) : isCompleted ? (
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    ) : (
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    )}
                  </div>

                  {/* Connecting Line */}
                  {!isLast && (
                    <div
                      className={`w-[2px] flex-1 my-0.5 rounded-full ${isLineActive ? "bg-emerald-500" : "bg-slate-200"
                        }`}
                    />
                  )}
                </div>

                {/* 3. Right: Content (Title + Timestamp + Detail) */}
                <div className={`flex-1 min-w-0 ${!isLast ? "pb-4" : "pb-1"} pt-1`}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <h4
                      className={`text-xs font-bold leading-tight truncate ${isCanceledStep
                        ? "text-rose-700"
                        : isPending
                          ? "text-slate-400 font-semibold"
                          : "text-slate-800"
                        }`}
                    >
                      {step.title}
                    </h4>

                    {isCanceledStep && (
                      <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
                        ยกเลิก
                      </span>
                    )}
                    {isCompleted && (
                      <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                        เสร็จสิ้น
                      </span>
                    )}
                    {isCurrent && !isCompleted && !isCanceledStep && (
                      <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                        กำลังดำเนินการ
                      </span>
                    )}
                  </div>

                  {/* Timestamp */}
                  <p
                    className={`text-[11px] mt-0.5 font-medium ${step.time
                      ? "text-slate-500"
                      : "text-slate-400 italic"
                      }`}
                  >
                    {step.time || "ยังไม่มีข้อมูลเวลา"}
                  </p>

                  {/* Description note */}
                  {step.desc && (
                    <p
                      className={`text-[11px] mt-0.5 leading-snug line-clamp-2 ${isPending ? "text-slate-400" : "text-slate-600"
                        }`}
                    >
                      {step.desc}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};


